import { isAxiosError } from 'axios';

/**
 * Turn an API error into a human-readable string for the admin UI.
 *
 * RFL-201 / RFL-203: QA repeatedly hit opaque "Validation failed" /
 * "Internal server error" messages that hid the real cause. The backend's
 * ZodValidationPipe returns a `issues[]` array ({path, message}); surface
 * those so a 400 tells you *which field* failed. For a server error with
 * no useful message, append the status code so it's clearly a backend
 * fault, not a silent no-op.
 */
export function extractApiError(err: unknown): string {
  if (isAxiosError(err)) {
    const status = err.response?.status;
    const data = err.response?.data as
      | { message?: string | string[]; issues?: { path: string; message: string }[] }
      | undefined;

    if (data?.issues !== undefined && data.issues.length > 0) {
      return data.issues
        .map((i) => (i.path === '' ? i.message : `${i.path}: ${i.message}`))
        .join('; ');
    }

    const message = Array.isArray(data?.message) ? data.message.join('; ') : data?.message;
    if (message !== undefined && message !== '' && message !== 'Internal server error') {
      return message;
    }
    if (status !== undefined && status >= 500) {
      return `Server error (${status}) — the request could not be completed. Please retry; if it persists, contact an administrator.`;
    }
    return message ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}
